import React, { useEffect, useRef, useState } from 'react';

/**
 * Small chevron badge that opens a device-picker dropdown, meant to sit
 * right next to a mic/camera CallBtn — mirrors the arrow next to Zoom's
 * mute/video buttons. `sections` is a list of { label, devices, currentDeviceId, onSelect }
 * so the same component covers both the camera-only menu and the
 * mic+speaker combined menu.
 */
export default function DeviceMenu({ sections, large }) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setIsOpen(false);
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const badgeSize = large ? 16 : 12;

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={() => setIsOpen(v => !v)}
        title="Switch device"
        style={{
          width: badgeSize, height: badgeSize, borderRadius: '50%', border: 'none',
          background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, marginLeft: 2, padding: 0,
        }}
      >
        <svg width={badgeSize - 6} height={badgeSize - 6} fill="currentColor" viewBox="0 0 16 16">
          <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708" />
        </svg>
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
            marginBottom: 8, minWidth: 190, maxWidth: 260,
            background: 'rgba(28,28,30,0.96)', backdropFilter: 'blur(12px)',
            borderRadius: 10, padding: '6px 0', boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            zIndex: 2147483647, fontSize: 12,
          }}
        >
          {sections.map((section, i) => (
            <div key={section.label}>
              {i > 0 && <div style={{ height: 1, background: 'rgba(255,255,255,0.12)', margin: '4px 0' }} />}
              <div style={{ padding: '4px 12px', color: 'rgba(255,255,255,0.5)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {section.label}
              </div>
              {section.devices.length === 0 && (
                <div style={{ padding: '6px 12px', color: 'rgba(255,255,255,0.4)' }}>No devices found</div>
              )}
              {section.devices.map((d, idx) => (
                <button
                  key={d.deviceId || idx}
                  onClick={() => { section.onSelect(d.deviceId); setIsOpen(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    padding: '6px 12px', background: 'none', border: 'none',
                    color: '#fff', cursor: 'pointer', textAlign: 'left',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}
                >
                  <span style={{ width: 12, flexShrink: 0 }}>{d.deviceId === section.currentDeviceId ? '✓' : ''}</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.label || 'Device'}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
